import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Avatar,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";

function ExcursionDetailsModal({ open, onClose, excursionData }) {
  // Assurez-vous que attendances est toujours un tableau.
  const [attendances, setAttendances] = useState([]);
  const [editModes, setEditModes] = useState({}); // Par exemple : { clientId_depart: true, clientId_arrived: true }
  const [localNotes, setLocalNotes] = useState({});
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);

  // Conversion de la donnée attendances en tableau (si nécessaire)
  useEffect(() => {
    let att;
    if (excursionData.attendances) {
      att = Array.isArray(excursionData.attendances)
        ? excursionData.attendances
        : Object.values(excursionData.attendances);
    } else {
      att = [];
    }
    setAttendances(att);
  }, [excursionData]);

  // Fonctions pour obtenir la note "depart" d'un client
  const getClientNote = (clientId) => {
    const client = attendances.find((att) => att.id === clientId);
    return client ? client.note || "" : "";
  };

  // Pour "arrived"
  const getClientNoteArrived = (clientId) => {
    const client = attendances.find((att) => att.id === clientId);
    return client ? client.noteArrived || "" : "";
  };

  // Mise à jour locale des notes
  const handleNoteChange = (key, event) => {
    const newText = event.target.value;
    setLocalNotes((prev) => ({ ...prev, [key]: newText }));
  };

  // Bascule du mode édition et sauvegarde de la note dans Firestore
  // Le paramètre isArrived détermine la zone (true pour "arrived", false pour "depart")
  const handleToggleEdit = (clientId, isArrived = false) => {
    const key = clientId + (isArrived ? "_arrived" : "_depart");
    const isEditing = editModes[key] || false;
    if (isEditing) {
      const newNote =
        localNotes[key] !== undefined
          ? localNotes[key]
          : isArrived
          ? getClientNoteArrived(clientId)
          : getClientNote(clientId);
      const updatedAttendances = attendances.map((att) =>
        att.id === clientId
          ? {
              ...att,
              ...(isArrived ? { noteArrived: newNote } : { note: newNote }),
            }
          : att
      );
      setAttendances(updatedAttendances);
      const docRef = doc(db, "clientExcursions", excursionData.id);
      updateDoc(docRef, { attendances: updatedAttendances }).catch((error) =>
        console.error("Error saving note:", error)
      );
    }
    setEditModes((prev) => ({ ...prev, [key]: !isEditing }));
  };

  // Gestion des changements de checkbox pour "depart" et "arrived"
  const handleCheckboxChange = async (clientId, field, event) => {
    const newValue = event.target.checked;
    const updatedAttendances = attendances.map((att) =>
      att.id === clientId ? { ...att, [field]: newValue } : att
    );
    setAttendances(updatedAttendances);
    try {
      const docRef = doc(db, "clientExcursions", excursionData.id);
      await updateDoc(docRef, { attendances: updatedAttendances });
    } catch (error) {
      console.error("Error updating attendance:", error);
    }
  };

  const getCheckboxValue = (clientId, field) => {
    const client = attendances.find((att) => att.id === clientId);
    return client ? client[field] || false : false;
  };

  // Gestion du clic sur l'avatar pour agrandir l'image
  const handleAvatarClick = (client) => {
    const url =
      client.profilePicture ||
      "https://via.placeholder.com/150/CCCCCC/FFFFFF/?text=Avatar";
    setEnlargedImage(url);
    setOpenImageDialog(true);
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    setEnlargedImage(null);
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    maxHeight: "90vh",
    overflowY: "auto",
  };

  useEffect(() => {
    // On écoute le document de l'excursion pour obtenir les attendances en temps réel
    const docRef = doc(db, "clientExcursions", excursionData.id);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const updatedAttendances = docSnap.data().attendances;
        // Comparaison pour n'update que si les données sont différentes
        if (
          JSON.stringify(updatedAttendances) !== JSON.stringify(attendances)
        ) {
          setAttendances(updatedAttendances);
        }
      }
    });
    return () => unsubscribe();
  }, [excursionData.id, attendances]);

  return (
    <Modal open={open} onClose={onClose}>
      <>
        <Box sx={modalStyle}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {`Excursion - ${excursionData.date} - ${excursionData.shift.time} - ${excursionData.shift.name}`}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Avatar & Name</TableCell>
                  <TableCell>Depart</TableCell>
                  <TableCell>Arrived</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendances.map((client) => (
                  <TableRow
                    key={client.id}
                    sx={{
                      backgroundColor:
                        getCheckboxValue(client.id, "depart") &&
                        getCheckboxValue(client.id, "arrived")
                          ? "lightgreen"
                          : "inherit",
                    }}
                  >
                    {/* Avatar & Name */}
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          width: "100%",
                          flexDirection: "column",
                        }}
                      >
                        <Avatar
                          src={
                            client.profilePicture ||
                            "https://via.placeholder.com/40/CCCCCC/FFFFFF/?text=Avatar"
                          }
                          alt={`${client.firstName} ${client.lastName}`}
                          sx={{ width: 40, height: 40, cursor: "pointer" }}
                          onClick={() => handleAvatarClick(client)}
                        />
                        <Typography
                          variant="body1"
                          style={{
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100px",
                          }}
                        >
                          {client.firstName} {client.lastName}
                        </Typography>
                      </Box>
                    </TableCell>
                    {/* Depart Column */}
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={getCheckboxValue(client.id, "depart")}
                              onChange={(e) =>
                                handleCheckboxChange(client.id, "depart", e)
                              }
                            />
                          }
                          label="Depart"
                        />
                        {editModes[client.id + "_depart"] ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <TextField
                              multiline
                              rows={2}
                              value={
                                localNotes[client.id + "_depart"] !== undefined
                                  ? localNotes[client.id + "_depart"]
                                  : getClientNote(client.id)
                              }
                              onChange={(e) =>
                                handleNoteChange(client.id + "_depart", e)
                              }
                              variant="outlined"
                              size="small"
                              sx={{
                                maxWidth: 200,
                                height: 48,
                                overflow: "auto",
                              }}
                              InputProps={{
                                sx: {
                                  padding: "4px 8px",
                                  fontSize: "0.875rem",
                                  lineHeight: 1.5,
                                },
                              }}
                            />
                            <IconButton
                              onClick={() => handleToggleEdit(client.id, false)}
                              size="small"
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <TextField
                              value={getClientNote(client.id) || "No note"}
                              variant="outlined"
                              size="small"
                              disabled
                              multiline
                              sx={{
                                maxWidth: 200,
                                height: 48,
                                overflow: "auto",
                              }}
                              InputProps={{
                                sx: {
                                  padding: "4px 8px",
                                  fontSize: "0.875rem",
                                  lineHeight: 1.5,
                                },
                              }}
                            />
                            <IconButton
                              onClick={() => handleToggleEdit(client.id, false)}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    {/* Arrived Column */}
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={getCheckboxValue(client.id, "arrived")}
                              onChange={(e) =>
                                handleCheckboxChange(client.id, "arrived", e)
                              }
                            />
                          }
                          label="Arrived"
                        />
                        {editModes[client.id + "_arrived"] ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <TextField
                              multiline
                              rows={2}
                              value={
                                localNotes[client.id + "_arrived"] !== undefined
                                  ? localNotes[client.id + "_arrived"]
                                  : getClientNoteArrived(client.id)
                              }
                              onChange={(e) =>
                                handleNoteChange(client.id + "_arrived", e)
                              }
                              variant="outlined"
                              size="small"
                              sx={{
                                maxWidth: 200,
                                height: 48,
                                overflow: "auto",
                              }}
                              InputProps={{
                                sx: {
                                  padding: "4px 8px",
                                  fontSize: "0.875rem",
                                  lineHeight: 1.5,
                                },
                              }}
                            />
                            <IconButton
                              onClick={() => handleToggleEdit(client.id, true)}
                              size="small"
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <TextField
                              value={
                                getClientNoteArrived(client.id) || "No note"
                              }
                              variant="outlined"
                              size="small"
                              disabled
                              multiline
                              sx={{
                                maxWidth: 200,
                                height: 48,
                                overflow: "auto",
                              }}
                              InputProps={{
                                sx: {
                                  padding: "4px 8px",
                                  fontSize: "0.875rem",
                                  lineHeight: 1.5,
                                },
                              }}
                            />
                            <IconButton
                              onClick={() => handleToggleEdit(client.id, true)}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {/* Modal pour agrandir l'avatar */}
        <Modal open={openImageDialog} onClose={handleCloseImageDialog}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 500,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
            }}
          >
            {enlargedImage && (
              <>
                <img
                  src={enlargedImage}
                  alt="Enlarged Avatar"
                  style={{
                    width: "100%",
                    maxWidth: "300px",
                    display: "block",
                    margin: "auto",
                  }}
                />
                <Typography variant="h6" sx={{ mt: 1, textAlign: "center" }}>
                  {(() => {
                    const client = attendances.find(
                      (c) => c.profilePicture === enlargedImage
                    );
                    return client
                      ? `${client.firstName} ${client.lastName}`
                      : "";
                  })()}
                </Typography>
              </>
            )}
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <IconButton onClick={handleCloseImageDialog}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </Modal>
      </>
    </Modal>
  );
}

export default ExcursionDetailsModal;
